import { useEffect, useRef, useState } from "react";
import "../App.css";
import { Typography, Skeleton, Box, Grid } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import StarIcon from '@mui/icons-material/Star';
import MovieID from "../api/MovieId";
import Video from "../api/Videos";
import { useParams } from "react-router-dom";
import Credit from "../api/Credit";
import useDragScroll from "../hooks/useDragScroll";

const MAX_IFRAMES = 3;
const MAX_CAST = 20;

const VideoSkeleton = () => (
  <Box sx={{ position: 'relative', paddingBottom: '56.25%', bgcolor: '#2A2A2B', borderRadius: 1 }}>
    <Skeleton
      variant="rectangular"
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        bgcolor: '#3A3A3B',
        borderRadius: 1,
      }}
      animation="wave"
    />
  </Box>
);

const MovieInfoSkeleton = () => (
  <div className="trailer">
    <div style={{ flex: 1 }}>
      <Skeleton variant="text" width="60%" height={40} sx={{ bgcolor: '#3A3A3B' }} animation="wave" />
      <Skeleton variant="text" width="40%" height={24} sx={{ bgcolor: '#3A3A3B', mt: 1 }} animation="wave" />
    </div>
  </div>
);

const OverviewSkeleton = () => (
  <div className="movieTr">
    <Skeleton variant="text" width={100} height={28} sx={{ bgcolor: '#3A3A3B' }} animation="wave" />
    <Skeleton variant="text" width="100%" height={20} sx={{ bgcolor: '#3A3A3B', mt: 2 }} animation="wave" />
    <Skeleton variant="text" width="100%" height={20} sx={{ bgcolor: '#3A3A3B', mt: 1 }} animation="wave" />
    <Skeleton variant="text" width="80%" height={20} sx={{ bgcolor: '#3A3A3B', mt: 1 }} animation="wave" />
  </div>
);

const CastSkeleton = () => (
  <div className="cast" aria-hidden="true">
    {[...Array(8)].map((_, index) => (
      <div key={index} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 80 }}>
        <Skeleton variant="circular" width={80} height={80} sx={{ bgcolor: '#3A3A3B' }} animation="wave" />
        <Skeleton variant="text" width={70} height={20} sx={{ bgcolor: '#3A3A3B', mt: 1 }} animation="wave" />
      </div>
    ))}
  </div>
);

const VideoEmbed = ({ videoKey, title }) => (
  <div style={{ position: 'relative', height: '0', paddingBottom: '56.25%' }}>
    <iframe
      style={{ position: 'absolute', top: '0', left: '0', width: '100%', height: '100%' }}
      src={`https://www.youtube.com/embed/${videoKey}`}
      title={title}
      frameBorder="0"
      loading="lazy"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    />
  </div>
);

const getInitial = (name = '') => (name.trim()[0] || '?').toUpperCase();

const Moviedetails = () => {
  const [trend, setTrend] = useState(null);
  const [video, setVideo] = useState(null);
  const [credit, setCredit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { id } = useParams();
  const castRef = useRef(null);

  useDragScroll(castRef);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setTrend(null);
    setVideo(null);
    setCredit(null);
    setError(null);

    Promise.all([
      MovieID(id, { signal: controller.signal }),
      Video(id, { signal: controller.signal }),
      Credit(id, { signal: controller.signal }),
    ])
      .then(([movieRes, videoRes, creditRes]) => {
        if (controller.signal.aborted) return;
        setTrend(movieRes.data);
        setVideo(videoRes.data);
        setCredit(creditRes.data);
      })
      .catch((err) => {
        if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        setError("Could not load this movie. Please try again.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [id]);

  const isVideoLoading = loading || !video?.results;
  const isMovieLoading = loading || !trend;
  const isCastLoading = loading || !credit?.cast;

  const videoResults = video?.results?.slice(0, MAX_IFRAMES) ?? [];
  const cast = credit?.cast?.slice(0, MAX_CAST) ?? [];

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ color: '#fff' }} role="alert">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <div>
      <Typography
        variant="h1"
        component="h1"
        sx={{
          color: '#fff',
          fontSize: { xs: '1.5rem', md: '2rem' },
          fontWeight: 700,
          mt: 2,
          mb: 2,
        }}
      >
        {isMovieLoading ? <Skeleton width="40%" sx={{ bgcolor: '#3A3A3B' }} /> : trend.title}
      </Typography>

      <Grid container spacing={1} color={"white"}>
        <Grid item xs={12} md={8}>
          {isVideoLoading ? (
            <VideoSkeleton />
          ) : videoResults[0] ? (
            <VideoEmbed videoKey={videoResults[0].key} title={`${trend?.title || 'Movie'} – primary trailer`} />
          ) : (
            <Box sx={{ aspectRatio: '16/9', bgcolor: '#2A2A2B', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1 }}>
              <Typography sx={{ color: '#B3B3B3' }}>No trailer available</Typography>
            </Box>
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          <Grid container spacing={0} color={"white"}>
            {[1, 2].map((idx) => (
              <Grid item xs={6} md={12} key={idx}>
                {isVideoLoading ? (
                  <VideoSkeleton />
                ) : videoResults[idx] ? (
                  <VideoEmbed videoKey={videoResults[idx].key} title={`${trend?.title || 'Movie'} – clip ${idx + 1}`} />
                ) : null}
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>

      <Grid container spacing={1} color={"white"}>
        <Grid item xs={12} md={8}>
          {isMovieLoading ? (
            <MovieInfoSkeleton />
          ) : (
            <div className="trailer">
              <div>
                <Typography variant="body1" sx={{ color: '#B3B3B3' }}>
                  {trend.release_date} | {trend.runtime}m | {trend.genres?.length > 0 ? trend.genres.map((g) => g.name).join(', ') : 'No genres'}
                </Typography>
              </div>
              <div className="heart1">
                <StarIcon aria-hidden="true" sx={{ color: '#F6C703' }} />
                <Typography
                  variant="subtitle1"
                  component="span"
                  color={'#ffffff'}
                  aria-label={`Rating: ${trend.vote_average?.toFixed(1)} out of 10`}
                >
                  {trend.vote_average?.toFixed(1)}
                </Typography>
              </div>
            </div>
          )}
        </Grid>
      </Grid>

      <Grid container spacing={1} color={"white"}>
        <Grid item xs={12} md={8}>
          {isMovieLoading ? (
            <OverviewSkeleton />
          ) : (
            <div className="movieTr">
              <Typography variant="h2" component="h2" sx={{ color: '#F6C703', fontSize: '1.25rem', mb: 2 }}>
                Overview
              </Typography>
              <Typography variant="body1" sx={{ color: '#B3B3B3' }}>
                {trend.overview}
              </Typography>
            </div>
          )}
        </Grid>
      </Grid>

      <div>
        <Typography variant="h2" component="h2" sx={{ color: '#FFFFFF', fontSize: '1.25rem', mb: 2 }}>
          Cast & Crew
        </Typography>
        {isCastLoading ? (
          <CastSkeleton />
        ) : (
          <div ref={castRef} className="cast">
            {cast.map((member) => (
              <div key={member.credit_id} style={{ minWidth: 80, textAlign: 'center' }}>
                {member.profile_path ? (
                  <Avatar
                    sx={{ width: 80, height: 80 }}
                    alt={member.name}
                    src={`https://image.tmdb.org/t/p/w185${member.profile_path}`}
                  />
                ) : (
                  <Avatar sx={{ width: 80, height: 80, bgcolor: '#3A3A3B', color: '#fff' }} aria-label={member.name}>
                    {getInitial(member.name)}
                  </Avatar>
                )}
                <Typography variant="body2" sx={{ color: '#B3B3B3', textWrap: 'wrap', mt: 1 }}>
                  {member.name}
                </Typography>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Moviedetails;
